import { FC } from "react";
import { VerificationNames } from "shared/types";
import { Checkbox } from "shared/components";
import { useTemplateContext } from "../provider";
import { StepDetails } from "../../../../types/landlord/verificationTemplate";
import { CheckComponent } from "../components/CheckComponent";

type Props = {
  details: StepDetails[];
  toggleServiceEnabled?: () => void;
  toggleItemServiceRequired: (v: string[]) => void;
};

const keys = {
  "housing-history.title": "Housing history",
  "housing-history.description": "Past housing history in the last two years",
  "housing-history.steps.address.ui.item-title": "Address",
  "housing-history.steps.address.title":
    "Add details about your housing history from the last two years",
  "housing-history.steps.address.description":
    "Add details about your housing history from the last two years, \nstarting with the most recent first",
  "housing-history.steps.address.housing-type": "Housing status",
  "housing-history.steps.address.housing-type.enum.rent": "Rent",
  "housing-history.steps.address.housing-type.enum.own-my-home": "Own my home",
  "housing-history.steps.address.housing-type.enum.other": "Other",
  "housing-history.steps.address.address": "Address",
  "housing-history.steps.address.separator-text": "Add address",
  "housing-history.steps.address.lease-document.separator-text":
    "Verify your residence",
  "housing-history.steps.address.monthly-payment.separator-text":
    "Add residence details",
  "housing-history.steps.address.housing-status.separator-text":
    "Add residence details",
  "housing-history.steps.address.landlord-name.separator-text":
    "Add landlord information",
  "housing-history.steps.address.reason-for-leaving.placeholder":
    "Why did you leave?",
  "housing-history.steps.address.landlord-details.landlord-name.title":
    "Landlord / Company Name",
  "housing-history.steps.address.landlord-details.landlord-phone.title":
    "Landlord / Company Phone",
  "housing-history.steps.address.landlord-details.landlord-email.title":
    "Landlord / Company Email",
  "housing-history.steps.address.lease-details.lease-document.title":
    "Upload lease",
  "housing-history.steps.address.lease-details.lease-document.description":
    "Speed up approval by uploading your lease",
  "housing-history.steps.address.lease-details.monthly-payment.title":
    "Monthly payment",
  "housing-history.steps.address.move-in-residency.move-in-date.title":
    "Move-in/out information",
  "housing-history.steps.address.move-in-residency.current-residence.title":
    "This is my current residence",
  "housing-history.steps.address.move-in-residency.reason-for-leaving.title":
    "Reason for leaving",
  "housing-history.steps.address.moved-out.move-out-date.title":
    "Move-out date",
  "housing-history.steps.address.monthly-mortgage-payment.title":
    "Monthly payment",
  "housing-history.steps.address.other.housing-status":
    "Tell us more about your housing status",
  "housing-history.steps.address.other.housing-status.enum.with-family-or-friends":
    "With family or friends",
  "housing-history.steps.address.other.housing-status.enum.in-a-dorm-or-student-housing":
    "In a dorm or student housing",
  "housing-history.steps.address.other.housing-status.enum.in-military-housing":
    "In military housing",
  "housing-history.steps.address.other.housing-status.enum.other": "Other",
  "housing-history.steps.address.landlord-details.lease-document.title":
    "Upload lease document"
};

const hiddenFields = ["current_residence"];

const enumValue = {
  rent: "rent",
  own_home: "own",
  other: "other"
};

export const HousingHistoryVerificationSlice: FC<Props> = ({
  details,
  toggleItemServiceRequired
}) => {
  const { readOnly, partnerType, template, selectedVerificationSchema } =
    useTemplateContext();

  const housingHistorySettings =
    template?.verification_schemas[partnerType][
      VerificationNames.HousingHistory
    ];
  const disabledService =
    !housingHistorySettings || !housingHistorySettings.address_step_enabled;

  const fieldRequired =
    housingHistorySettings?.[`${selectedVerificationSchema}_option_required`];

  const schemaTitle = (() => {
    switch (selectedVerificationSchema) {
      case "rent":
        return "Rent";
      case "own_home":
        return "Own";
      case "other":
        return "Other";
      default:
        return "Unknown";
    }
  })();

  return (
    <div className="rounded-lg bg-white p-8 border border-alpha-100 shadow-xs">
      <p className="text-lg font-bold text-content-1000">{schemaTitle}</p>

      <p className="mb-4 text-sm font-medium leading-5 text-content-400 border-t border-alpha-40 pt-4 mt-4">
        This information will be requested from the applicant if they add the
        housing type ‘{enumValue[selectedVerificationSchema]}’
      </p>

      <div className="space-y-2">
        {Object.entries((details as any).properties).map(
          ([key, value], index: number) => {
            if (hiddenFields.includes(key)) return null;
            const property = value as { title: string };

            const title =
              keys[property.title.replace("verifications.", "")] ||
              property.title;

            const isRequired = Boolean(fieldRequired?.[key] ?? true);

            return (
              <div
                key={index}
                className="flex justify-between mb-2 rounded-lg border border-alpha-80 bg-alpha-20 px-4 py-3"
              >
                <p className="text-sm font-medium text-content-500">{title}</p>

                <div>
                  {readOnly ? (
                    <CheckComponent
                      checked={isRequired}
                      label={
                        isRequired ? (
                          "Required"
                        ) : (
                          <p className="text-content-200">Optional</p>
                        )
                      }
                    />
                  ) : fieldRequired?.[key] !== undefined ? (
                    <Checkbox
                      checked={isRequired}
                      label="Required"
                      disabled={disabledService}
                      onChange={() =>
                        toggleItemServiceRequired([
                          "verification_schemas",
                          partnerType,
                          VerificationNames.HousingHistory,
                          `${selectedVerificationSchema}_option_required`,
                          key
                        ])
                      }
                    />
                  ) : null}
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};
